import type { CoachingGradingValueType } from "@repo/app-types/entities/coachingScore";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { useState, type FC, type FocusEvent } from "react";
import { Dialog, DialogTrigger } from "react-aria-components";
import { twJoin } from "tailwind-merge";
import { InfoButton } from "../Buttons/infoButton.js";
import { Size } from "../helpers/helpers.js";
import { CenteredModal } from "../Modal/centeredModal.js";
import { ModalHeader } from "../Modal/modalHeader.js";
import { skeletonStyle } from "../Skeletons/skeletons.js";
import {
  CoachingGoalTrendMeter,
  TREND_CHART_HEIGHT,
} from "./coachingGoalTrendMeter.js";
import type {
  CoachingGoalCallPoint,
  CoachingGoalStatusTone,
} from "./coachingGoalTypes.js";

export interface CoachingGoalSummaryPanelProps {
  criterionName: string;
  /** Grading context / description shown in the criterion info popover. */
  criterionInfo?: string;
  impactLabel: string;
  startedScore: number;
  targetScore: number;
  gradingValueType: CoachingGradingValueType;
  scorePath: readonly number[];
  dayProgress: number;
  statusLabel: string;
  statusTone: CoachingGoalStatusTone;
  calls: readonly CoachingGoalCallPoint[];
  activeCallId?: string;
  onCallOpen?: (gradingId: GradingId) => void;
  /** Renders a fixed-height chart skeleton while the calls load. */
  isChartLoading?: boolean;
}

export const CoachingGoalSummaryPanel: FC<CoachingGoalSummaryPanelProps> = (
  props,
) => {
  const {
    criterionName,
    criterionInfo,
    impactLabel,
    startedScore,
    targetScore,
    gradingValueType,
    scorePath,
    dayProgress,
    statusLabel,
    statusTone,
    calls,
    activeCallId,
    onCallOpen,
    isChartLoading,
  } = props;

  const [isInfoVisible, setIsInfoVisible] = useState(false);

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsInfoVisible(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-border-light bg-gray-50 p-4"
      onBlur={handleBlur}
      onFocus={() => setIsInfoVisible(true)}
      onMouseEnter={() => setIsInfoVisible(true)}
      onMouseLeave={() => setIsInfoVisible(false)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-auto items-center gap-1.5">
          <p className="w-0 max-w-max flex-auto truncate text-[0.8125rem] leading-5 font-medium text-gray-500">
            {criterionName}
          </p>

          {criterionInfo ? (
            <DialogTrigger>
              <InfoButton
                aria-label={`About ${criterionName}`}
                className={twJoin(
                  "flex-none opacity-0 focus:opacity-100",
                  isInfoVisible && "opacity-100",
                )}
              />

              <CenteredModal isDismissable size={Size.LARGE}>
                <Dialog className="space-y-4 p-4 outline-hidden">
                  <ModalHeader isCloseVisible>{criterionName}</ModalHeader>
                  <p className="text-sm whitespace-pre-wrap text-foreground">
                    {criterionInfo}
                  </p>
                </Dialog>
              </CenteredModal>
            </DialogTrigger>
          ) : null}
        </div>

        <p className="flex-none text-[0.8125rem] leading-5 font-medium whitespace-nowrap text-green-700">
          {impactLabel}
        </p>
      </div>

      <div className="mt-2">
        {isChartLoading ? (
          <div
            className={skeletonStyle({
              className: "rounded-md",
            })}
            style={{ height: TREND_CHART_HEIGHT }}
            role="status"
            aria-label="Loading trend"
          />
        ) : (
          <CoachingGoalTrendMeter
            calls={calls}
            gradingValueType={gradingValueType}
            startedScore={startedScore}
            targetScore={targetScore}
            scorePath={scorePath}
            dayProgress={dayProgress}
            statusLabel={statusLabel}
            statusTone={statusTone}
            activeCallId={activeCallId}
            onCallOpen={onCallOpen}
          />
        )}
      </div>

      {!isChartLoading && calls.length === 0 ? (
        <p className="mt-1 text-xs font-medium text-muted-foreground">
          No calls yet this week
        </p>
      ) : null}
    </div>
  );
};
